import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/Select'
import type { SalesPeriodKey, SalesPeriodSelection } from '@/lib/api/deep-dive.types'
import { PERIOD_KEYS, PERIOD_LABELS } from '@/lib/system-intelligence'

/** The endpoint's own ceiling; past it the answer is 400 `Range too large`. */
const MAX_SPAN_DAYS = 366

/** Today in UTC — no sales can be recorded after it. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

/** `YYYY-MM-DD`, `days` from `iso`. */
function shift(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

interface PeriodPickerProps {
  value: SalesPeriodSelection
  onChange: (next: SalesPeriodSelection) => void
  /** Tighter controls, for a page tuned to fit the viewport. */
  compact?: boolean
  /** Hover text on the control — the dates the chosen period resolved to. */
  title?: string | false
}

/**
 * The period the sales figures cover.
 *
 * A dropdown rather than a segmented control: nine options is well past what a
 * row of chips can hold, and unlike the window switches elsewhere these are not
 * a quick back-and-forth comparison — you pick a period and read it.
 *
 * The from/to inputs appear only for `custom`, because for every other period
 * the server decides the dates and an editable pair would suggest otherwise.
 * They are bounded to the endpoint's own limits — no future dates, ends that
 * constrain each other, and a span capped at `MAX_SPAN_DAYS` — so the 400s the
 * API defines (`Invalid from`, `Invalid range`, `Range too large`) are prevented
 * rather than discovered.
 */
export function PeriodPicker({ value, onChange, compact, title }: PeriodPickerProps) {
  const isCustom = value.key === 'custom'

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label
        title={title || undefined}
        className={cn(
          'border-line bg-surface hover:border-line-bright flex items-center gap-2 rounded-lg border',
          'focus-within:border-primary/60 focus-within:ring-primary/25 transition-colors focus-within:ring-2',
          compact ? 'px-3 py-1' : 'px-3 py-1.5',
        )}
      >
        <CalendarDays aria-hidden className="text-fg-subtle size-3.5" />
        <span className="text-fg-subtle font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          Period:
        </span>
        <Select
          value={value.key}
          onChange={(next) =>
            onChange({
              ...value,
              key: next as SalesPeriodKey,
              // Seed a sensible fortnight so switching to Custom shows data at
              // once rather than an empty, erroring pair of inputs.
              ...(next === 'custom' && !value.from
                ? { from: shift(todayUtc(), -13), to: todayUtc() }
                : {}),
            })
          }
          aria-label="Period"
          className={cn(
            'w-auto border-0 bg-transparent pr-7 pl-0 font-mono text-[11px] font-semibold uppercase focus:ring-0',
            compact ? 'h-5' : 'h-7',
          )}
        >
          {PERIOD_KEYS.map((key) => (
            <option key={key} value={key}>
              {PERIOD_LABELS[key]}
            </option>
          ))}
        </Select>
      </label>

      {isCustom && (
        <label
          title={`Inclusive dates, up to ${MAX_SPAN_DAYS} days.`}
          className={cn(
            'border-line bg-surface hover:border-line-bright flex items-center gap-1.5 rounded-lg border',
            'focus-within:border-primary/60 focus-within:ring-primary/25 transition-colors focus-within:ring-2',
            compact ? 'px-3 py-1.5' : 'px-3 py-2',
          )}
        >
          <input
            type="date"
            aria-label="From"
            value={value.from ?? ''}
            max={value.to ?? todayUtc()}
            min={value.to ? shift(value.to, -(MAX_SPAN_DAYS - 1)) : undefined}
            onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
            className="text-fg bg-transparent font-mono text-[11px] font-semibold uppercase outline-none"
          />
          <span className="text-fg-subtle font-mono text-[11px]">→</span>
          <input
            type="date"
            aria-label="To"
            value={value.to ?? ''}
            min={value.from}
            max={
              value.from ? minDate(shift(value.from, MAX_SPAN_DAYS - 1), todayUtc()) : todayUtc()
            }
            onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
            className="text-fg bg-transparent font-mono text-[11px] font-semibold uppercase outline-none"
          />
        </label>
      )}
    </div>
  )
}

/** The earlier of two `YYYY-MM-DD` dates — string order is date order here. */
function minDate(a: string, b: string): string {
  return a < b ? a : b
}
